import { Location } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth, Tokens } from '@okta/okta-auth-js';
import { OktaSignIn } from '@okta/okta-signin-widget';
import { AuthService } from 'src/app/core/services/auth.service';
import { EnvironmentService } from 'src/app/core/services/environment.service';
import { SsoConfig } from 'th-env';

const DEFAULT_ORIGINAL_URI = window.location.origin;

@Component({
  selector: 'th-login-sso',
  templateUrl: './login-sso.component.html',
  styleUrls: ['./login-sso.component.scss']
})
export class LoginSsoComponent implements OnInit, OnDestroy {
  signIn: OktaSignIn;

  get ssoConfig(): SsoConfig {
    return this.environmentService.ssoConfig;
  }

  loading = false;

  constructor(
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private environmentService: EnvironmentService,
    private authService: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private location: Location
  ) {}

  async ngOnInit() {
    const skipLegacyRedirect = this.activatedRoute.snapshot.queryParamMap.get('skipLegacyRedirect') === 'true';

    if (this.environmentService.loginUrl === '/login-legacy' && !skipLegacyRedirect) {
      this.router.navigate([this.environmentService.loginUrl]);
    } else {
      this.loading = true;

      await this.initializeLoginForm().finally(() => (this.loading = false));
    }
  }

  ngOnDestroy() {
    this.signIn?.remove();
  }

  async initializeLoginForm() {
    this.signIn = new OktaSignIn({
      /**
       * Note: when using the Sign-In Widget for an OIDC flow, it still
       * needs to be configured with the base URL for your Okta Org. Here
       * we derive it from the given issuer for convenience.
       */
      baseUrl: this.ssoConfig.oidc.issuer.split('/oauth2')[0],
      clientId: this.ssoConfig.oidc.clientId,
      redirectUri: this.ssoConfig.oidc.redirectUri,
      logo: `assets/img/logo/${this.environmentService.logo}`,
      helpLinks: {
        custom: [
          {
            href: 'javascript:',
            text: 'For assistance logging in, please call our support team - 1.844.646.6300'
          }
        ]
      },
      i18n: {
        en: {
          'primaryauth.title': 'Sign in',
          'password.forgot.email.or.username.placeholder': 'Username',
          'password.forgot.email.or.username.tooltip': 'Username'
        }
      },
      // TODO: useClassicEngine will be deprecated in an upcoming release.
      // To use identity engine, we must enable it for the application in the Okta Admin Console
      useClassicEngine: true,
      features: {
        rememberMe: false
      }
    });

    const originalUri = this.oktaAuth.getOriginalUri();
    if (!originalUri || originalUri === DEFAULT_ORIGINAL_URI) {
      this.oktaAuth.setOriginalUri('/private');
    }

    try {
      const response = await this.oktaAuth.token.getWithoutPrompt();
      await this.oktaAuth.handleLoginRedirect(response.tokens);
    } catch (error) {
      this.authService.loginResponse = null;
      this.signIn
        .showSignInToGetTokens({
          el: '#sign-in-widget',
          scopes: this.ssoConfig.oidc.scopes
        })
        .then(async (tokens: Tokens) => {
          this.signIn.remove();

          this.loading = true;

          await this.oktaAuth.handleLoginRedirect(tokens);
        })
        .catch((err) => {
          throw err;
        });
    }
  }
}
